import React from 'react';
import './VerificationLog.css';

const VerificationLog = ({ sessions, linkId }) => {
  if (!sessions || sessions.length === 0) return null;

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDateOnly = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTimeOnly = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const calculateCompletionTime = () => {
    if (sessions.length < 2) return '0s';
    const start = new Date(sessions[0].timestamp);
    const end = new Date(sessions[sessions.length - 1].timestamp);
    const diffSeconds = Math.round((end - start) / 1000);
    return diffSeconds + 's';
  };

  const checkSameIP = () => {
    if (sessions.length < 2) return true;
    const firstIP = sessions[0].network?.ip;
    return sessions.every(s => s.network?.ip === firstIP);
  };

  const getLocation = () => {
    const lastSession = sessions[sessions.length - 1];
    if (!lastSession.network) return 'Unknown';
    return `${lastSession.network.city}, ${lastSession.network.country}`;
  };

  const getCountryCode = () => {
    const lastSession = sessions[sessions.length - 1];
    return lastSession.network?.country || 'Unknown';
  };

  // Only show completed sessions (not page loads)
  const completedSessions = sessions.filter(s => s.completed);
  
  if (completedSessions.length === 0) return null;
  
  const lastSession = completedSessions[completedSessions.length - 1];
  const attempts = completedSessions.length;

  return (
    <div className="vl-container">
      {/* Header */}
      <div className="vl-header">
        <h2>Identity Verification Log</h2>
        <div className="vl-header-meta">
          <span className="vl-link-id">Link ID: {linkId || '2904'} | {lastSession.device.type} User</span>
          <span className="vl-attempts">{attempts} Attempt{attempts !== 1 ? 's' : ''} | {formatDateOnly(lastSession.timestamp)}, {formatTimeOnly(lastSession.timestamp)}</span>
        </div>
        <div className="vl-status-badge">
          <span className="vl-badge-success">✓ Verification Complete</span>
        </div>
      </div>

      {/* Sessions Timeline */}
      {completedSessions.map((session, index) => (
        <div key={index} className="vl-session">
          <div className="vl-session-number">{index + 1}</div>
          
          <div className="vl-session-content">
            <div className="vl-session-header">
              <h3>{session.device.type} Verification Complete</h3>
              <span className="vl-timestamp">{formatDateOnly(session.timestamp)}, {formatTimeOnly(session.timestamp)}</span>
            </div>

            {/* Session Cards */}
            <div className="vl-cards-grid">
              {/* Device Card */}
              <div className="vl-card">
                <div className="vl-card-header">
                  <span className="vl-card-icon">💻</span>
                  <h4>Device</h4>
                </div>
                <div className="vl-card-content">
                  <div className="vl-info-row">
                    <span className="vl-label">Type:</span>
                    <span className="vl-value">{session.device.type}</span>
                  </div>
                  <div className="vl-info-row">
                    <span className="vl-label">OS:</span>
                    <span className="vl-value">{session.device.os}</span>
                  </div>
                  <div className="vl-info-row">
                    <span className="vl-label">CPU:</span>
                    <span className="vl-value">{session.device.cpu}</span>
                  </div>
                </div>
              </div>

              {/* Browser Card */}
              <div className="vl-card">
                <div className="vl-card-header">
                  <span className="vl-card-icon">🌐</span>
                  <h4>Browser</h4>
                </div>
                <div className="vl-card-content">
                  <div className="vl-info-row">
                    <span className="vl-label">Name:</span>
                    <span className="vl-value">{session.browser.browser}</span>
                  </div>
                  <div className="vl-info-row">
                    <span className="vl-label">Version:</span>
                    <span className="vl-value">{session.browser.version}</span>
                  </div>
                  <div className="vl-info-row">
                    <span className="vl-label">Engine:</span>
                    <span className="vl-value">{session.browser.engine}</span>
                  </div>
                </div>
              </div>

              {/* Network Card */}
              {session.network && (
                <div className="vl-card">
                  <div className="vl-card-header">
                    <span className="vl-card-icon">📡</span>
                    <h4>Network</h4>
                  </div>
                  <div className="vl-card-content">
                    <div className="vl-info-row">
                      <span className="vl-label">IP:</span>
                      <span className="vl-value">{session.network.ip}</span>
                    </div>
                    <div className="vl-info-row">
                      <span className="vl-label">ISP:</span>
                      <span className="vl-value">{session.network.org}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Geolocation - Show only on last session */}
            {session.network && index === completedSessions.length - 1 && (
              <div className="vl-geo-section">
                <div className="vl-geo-header">
                  <span className="vl-geo-icon">📍</span>
                  <h4>Geolocation Information</h4>
                  <span className="vl-geo-badge">{session.network.country}</span>
                </div>
                <div className="vl-geo-grid">
                  <div className="vl-geo-item">
                    <span className="vl-geo-label">CITY:</span>
                    <span className="vl-geo-value">{session.network.city}, {session.network.region}</span>
                  </div>
                  <div className="vl-geo-item">
                    <span className="vl-geo-label">POSTAL CODE:</span>
                    <span className="vl-geo-value">{session.network.postal}</span>
                  </div>
                  <div className="vl-geo-item">
                    <span className="vl-geo-label">COORDINATES:</span>
                    <span className="vl-geo-value">{session.network.loc}</span>
                  </div>
                  <div className="vl-geo-item">
                    <span className="vl-geo-label">TIMEZONE:</span>
                    <span className="vl-geo-value">{session.network.timezone}</span>
                  </div>
                  <div className="vl-geo-item">
                    <span className="vl-geo-label">ISP:</span>
                    <span className="vl-geo-value">{session.network.org}</span>
                  </div>
                  {session.network.hostname && (
                    <div className="vl-geo-item vl-full-width">
                      <span className="vl-geo-label">HOSTNAME:</span>
                      <span className="vl-geo-value">{session.network.hostname}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Network Security - Show only on last session */}
            {session.network && index === completedSessions.length - 1 && (
              <div className="vl-security-section">
                <div className="vl-security-header">
                  <span className="vl-security-icon">🔒</span>
                  <h4>Network Security Status</h4>
                </div>
                <div className="vl-security-grid">
                  <div className="vl-security-item">
                    <span className="vl-security-label">No VPN</span>
                    <span className={`vl-security-check ${!session.network.vpn ? 'pass' : 'fail'}`}>
                      {!session.network.vpn ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="vl-security-item">
                    <span className="vl-security-label">No Proxy</span>
                    <span className={`vl-security-check ${!session.network.proxy ? 'pass' : 'fail'}`}>
                      {!session.network.proxy ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="vl-security-item">
                    <span className="vl-security-label">Not Hosting</span>
                    <span className={`vl-security-check ${!session.network.hosting ? 'pass' : 'fail'}`}>
                      {!session.network.hosting ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="vl-security-item">
                    <span className="vl-security-label">No Relay</span>
                    <span className={`vl-security-check ${!session.network.relay ? 'pass' : 'fail'}`}>
                      {!session.network.relay ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="vl-security-item">
                    <span className="vl-security-label">No Tor</span>
                    <span className={`vl-security-check ${!session.network.tor ? 'pass' : 'fail'}`}>
                      {!session.network.tor ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Active Session Badge */}
            <div className="vl-session-status">
              <span className="vl-status-active">● Active Session</span>
            </div>
          </div>
        </div>
      ))}

      {/* Summary Footer */}
      <div className="vl-summary">
        <div className="vl-summary-item">
          <div className="vl-summary-number">{completedSessions.length}</div>
          <div className="vl-summary-label">TOTAL ATTEMPTS</div>
        </div>
        <div className="vl-summary-item">
          <div className="vl-summary-number">{calculateCompletionTime()}</div>
          <div className="vl-summary-label">COMPLETION TIME</div>
        </div>
        <div className="vl-summary-item">
          <div className="vl-summary-icon">{checkSameIP() ? '✓' : '✗'}</div>
          <div className="vl-summary-label">SAME IP ADDRESS</div>
        </div>
        <div className="vl-summary-item">
          <div className="vl-summary-flag">{getCountryCode()}</div>
          <div className="vl-summary-label">{getLocation()}</div>
        </div>
      </div>
    </div>
  );
};

export default VerificationLog;